import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from '../lib/apiTransport';
import { toast } from 'sonner';
import {
  getDobRetryMessage,
  isDobRequiredError,
  PARTICIPATION_INTENT_TYPES,
  saveParticipationDobIntent,
} from '../lib/participationDob';
import { isPrimaryHostJoinIneligible } from '../utils/gameParticipation';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

/**
 * SmsJoin — 2-tap join flow via SMS deep link.
 *
 * URL: /r/{runId}?u={userId}&t={token}
 *
 * Flow: validate token → auto-auth → show run → JOIN → "You're in"
 * Target: ≤5 seconds, ≤2 taps, mobile-first.
 */
export default function SmsJoin() {
  const { runId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const userId = searchParams.get('u');
  const token = searchParams.get('t');

  const [phase, setPhase] = useState('loading'); // loading | ready | joined | error
  const [run, setRun] = useState(null);
  const [error, setError] = useState('');
  const [joining, setJoining] = useState(false);
  const [jwt, setJwt] = useState(null);

  useEffect(() => {
    if (!location.state?.dobCompletionSuccess) return;
    toast.success(getDobRetryMessage(location.state.participationIntentType));
    navigate(`/r/${runId}`, { replace: true, state: null });
  }, [location.state, navigate, runId]);

  // Log event (fire-and-forget)
  const logEvent = useCallback((event, extra = {}) => {
    axios.post(`${API}/share/events`, {
      event_type: event, run_id: runId, source: 'sms_deeplink', ...extra,
    }).catch(() => {});
  }, [runId]);

  // Step 1: Auth + fetch run in parallel
  useEffect(() => {
    if (!runId) { setError('Invalid link'); setPhase('error'); return; }

    logEvent('sms_link_opened');

    const init = async () => {
      try {
        // Fetch run preview (no auth needed)
        const runResp = await axios.get(`${API}/runs/${runId}/preview`);
        setRun(runResp.data);

        if (runResp.data.is_cancelled) {
          setError('This run has been cancelled');
          setPhase('error');
          return;
        }

        // If we have token params, auto-auth
        if (userId && token) {
          try {
            const authResp = await axios.post(`${API}/r/auth`, { token, user_id: userId });
            const accessToken = authResp.data.access_token;
            setJwt(accessToken);
            // Persist session
            localStorage.setItem('access_token', accessToken);
            localStorage.setItem('user_id', authResp.data.user_id);

            // Check if already in the run
            const eligResp = await axios.get(`${API}/runs/${runId}/join-eligibility`, {
              headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (eligResp.data.reason_code === 'already_joined') {
              setPhase('joined');
              return;
            }
            if (isPrimaryHostJoinIneligible(eligResp.data)) {
              setError('The primary host cannot join this run as a player.');
              setPhase('error');
              return;
            }
          } catch (authErr) {
            const detail = authErr.response?.data?.detail || '';
            if (detail.includes('expired') || detail.includes('Invalid')) {
              setError('Link expired. Request a new invite.');
              setPhase('error');
              return;
            }
            // Auth failed but run loaded — show run as read-only
          }
        } else {
          // No token params — check if user has existing session
          const existing = localStorage.getItem('access_token');
          if (existing) {
            setJwt(existing);
            try {
              const eligResp = await axios.get(`${API}/runs/${runId}/join-eligibility`, {
                headers: { Authorization: `Bearer ${existing}` },
              });
              if (eligResp.data.reason_code === 'already_joined') {
                setPhase('joined');
                return;
              }
              if (isPrimaryHostJoinIneligible(eligResp.data)) {
                setError('The primary host cannot join this run as a player.');
                setPhase('error');
                return;
              }
            } catch {
              // Token might be expired — that's fine, they'll need a fresh link
            }
          }
        }

        setPhase('ready');
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Run not found');
        } else {
          setError('Unable to load run');
        }
        setPhase('error');
      }
    };

    init();
  }, [runId, userId, token, logEvent]);

  // Step 2: Join
  const handleJoin = async () => {
    if (!jwt || joining) return;
    setJoining(true);
    logEvent('join_clicked');

    try {
      await axios.post(`${API}/r/${runId}/join`, null, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      logEvent('join_success');
      setPhase('joined');
    } catch (err) {
      if (isDobRequiredError(err)) {
        const saved = saveParticipationDobIntent({
          type: PARTICIPATION_INTENT_TYPES.SMS_JOIN,
          returnTo: `/r/${runId}`,
        });
        if (saved) {
          window.location.assign('/complete-dob');
          return;
        }
      }
      const rawDetail = err.response?.data?.detail;
      const detail = typeof rawDetail === 'string'
        ? rawDetail
        : (rawDetail?.message || 'Unable to join');
      logEvent('join_failed', { reason: detail });

      if (detail.includes('already')) {
        setPhase('joined');
      } else {
        setError(detail);
        setPhase('error');
      }
    } finally {
      setJoining(false);
    }
  };

  // ── Format date ──
  const formatDate = (dt, tz) => {
    if (!dt) return '';
    try {
      const d = new Date(dt);
      return d.toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
        timeZone: tz || 'America/Chicago',
      });
    } catch {
      return '';
    }
  };

  // ── LOADING ──
  if (phase === 'loading') {
    return (
      <div data-testid="sms-join-loading" style={styles.container}>
        <div style={styles.spinner} />
      </div>
    );
  }

  // ── ERROR ──
  if (phase === 'error') {
    return (
      <div data-testid="sms-join-error" style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>!</div>
          <p style={styles.errorText}>{error}</p>
        </div>
      </div>
    );
  }

  // ── JOINED ──
  if (phase === 'joined') {
    return (
      <div data-testid="sms-join-confirmed" style={styles.container}>
        <div style={styles.card}>
          <div style={styles.checkmark}>&#10003;</div>
          <h1 style={styles.confirmedTitle}>You're in</h1>
          {run && (
            <>
              <p style={styles.runTitle}>{run.title}</p>
              <p style={styles.detail}>{formatDate(run.date_time, run.tz)}</p>
              <p style={styles.detail}>{run.location_name}</p>
            </>
          )}
          <a
            href={`/games/${runId}`}
            data-testid="sms-join-view-run"
            style={styles.viewLink}
            onClick={(e) => {
              e.preventDefault();
              // Full page reload so App.js re-reads the token from localStorage
              window.location.href = `/games/${runId}`;
            }}
          >
            View run details
          </a>
        </div>
      </div>
    );
  }

  // ── READY: Show run + JOIN button ──
  const spotsLeft = run?.spots_left ?? 0;
  const isFull = run?.is_full;

  return (
    <div data-testid="sms-join-ready" style={styles.container}>
      <div style={styles.card}>
        <h1 data-testid="sms-join-run-title" style={styles.title}>{run?.title}</h1>

        <div style={styles.details}>
          <div style={styles.detailRow}>
            <span style={styles.detailIcon}>&#128197;</span>
            <span data-testid="sms-join-date" style={styles.detailText}>
              {formatDate(run?.date_time, run?.tz)}
            </span>
          </div>
          <div style={styles.detailRow}>
            <span style={styles.detailIcon}>&#128205;</span>
            <span data-testid="sms-join-location" style={styles.detailText}>
              {run?.location_name}
            </span>
          </div>
          <div style={styles.detailRow}>
            <span style={styles.detailIcon}>&#127936;</span>
            <span data-testid="sms-join-spots" style={styles.detailText}>
              {isFull ? 'Full' : `${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} left`}
            </span>
          </div>
        </div>

        {run?.host_username && (
          <p style={styles.host}>Hosted by {run.host_username}</p>
        )}

        {jwt && !isFull ? (
          <button
            data-testid="sms-join-button"
            onClick={handleJoin}
            disabled={joining}
            style={{
              ...styles.joinBtn,
              opacity: joining ? 0.7 : 1,
            }}
          >
            {joining ? 'Joining...' : 'JOIN'}
          </button>
        ) : isFull ? (
          <div data-testid="sms-join-full" style={styles.fullBadge}>Run is full</div>
        ) : (
          <p style={styles.noAuth}>Sign in to join this run</p>
        )}
      </div>
    </div>
  );
}

// ── Inline styles (no external deps, fast paint) ──
const styles = {
  container: {
    minHeight: '100dvh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    padding: '16px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    width: '100%',
    maxWidth: '380px',
    textAlign: 'center',
    padding: '32px 24px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #e5e7eb',
    borderTop: '3px solid #18181b',
    borderRadius: '50%',
    animation: 'spin 0.6s linear infinite',
  },
  title: {
    fontSize: '28px',
    fontWeight: '800',
    color: '#18181b',
    margin: '0 0 24px 0',
    lineHeight: '1.1',
    letterSpacing: '-0.5px',
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '20px',
  },
  detailRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    justifyContent: 'center',
  },
  detailIcon: {
    fontSize: '18px',
    width: '24px',
    textAlign: 'center',
    flexShrink: 0,
  },
  detailText: {
    fontSize: '16px',
    color: '#3f3f46',
    textAlign: 'left',
  },
  host: {
    fontSize: '14px',
    color: '#71717a',
    marginBottom: '28px',
  },
  joinBtn: {
    width: '100%',
    padding: '16px',
    fontSize: '18px',
    fontWeight: '700',
    color: '#fff',
    background: '#18181b',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    letterSpacing: '1px',
    WebkitTapHighlightColor: 'transparent',
  },
  fullBadge: {
    padding: '14px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#71717a',
    background: '#f4f4f5',
    borderRadius: '12px',
  },
  noAuth: {
    fontSize: '14px',
    color: '#a1a1aa',
    marginTop: '8px',
  },
  // Confirmed state
  checkmark: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: '#16a34a',
    color: '#fff',
    fontSize: '28px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  confirmedTitle: {
    fontSize: '24px',
    fontWeight: '800',
    color: '#18181b',
    margin: '0 0 16px 0',
  },
  runTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#18181b',
    margin: '0 0 4px 0',
  },
  detail: {
    fontSize: '15px',
    color: '#52525b',
    margin: '2px 0',
  },
  viewLink: {
    display: 'inline-block',
    marginTop: '24px',
    fontSize: '14px',
    color: '#18181b',
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
  },
  // Error state
  errorIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: '#fef2f2',
    color: '#ef4444',
    fontSize: '24px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  errorText: {
    fontSize: '16px',
    color: '#3f3f46',
  },
};
